import Avatar from '@/common/components/atom/avatar';
import Button from '@/common/components/atom/button';
import BackIcon from '@/common/components/icons/back';
import LikeIcon from '@/common/components/icons/like';
import VerifyIcon from '@/common/components/icons/verify';
import { TomanPrice } from '@/common/components/atom/toman/toman';
import classNames from '@/common/utils/classNames';
import { getImageUrl } from '@/common/utils/getImageUrl';
import getConfig from 'next/config';
import ScrollContainer from 'react-indiana-drag-scroll';

const cdnBaseUrl = () => getConfig()?.publicRuntimeConfig?.CDN_BASE_URL;

export interface CarouselDoctor {
  id: string;
  name: string;
  expertise?: string;
  image?: string;
  isVerify: boolean;
  satisfaction?: number;
  ratesCount?: number;
  /** The line above the button, e.g. «اولین نوبت: امروز ساعت ۱۲:۱۰». */
  line?: string;
  /** Registered insurances; empty → no line (never «بیمه ندارد», the doctor may just not have filled it in). */
  insurances?: string[];
  /** How many supplementary insurances the doctor's profile lists (Biko `supplement`). */
  supplementCount?: number;
  /** Badge on top of the card (e.g. «منتخب مراجعان» for the plan's preferred doctors — picked from real bookings). */
  badge?: string;
  /** Patients' own office waiting time, only when it reads well («کمتر از یک ساعت»). */
  waitingTitle?: string;
  buttonText: string;
}

const faNumber = (value: number) => value.toLocaleString('fa-IR');

const hasInsurance = (doctor: CarouselDoctor) => !!doctor.insurances?.length || !!doctor.supplementCount;
const insuranceCount = (doctor: CarouselDoctor) => (doctor.insurances?.length ?? 0) + (doctor.supplementCount ?? 0);
/** The badge's short name, as people say it («بیمه تامین +۱»). */
const shortInsurance = (name: string) => (name.replace(/\s+/g, ' ').trim() === 'تامین اجتماعی' ? 'تامین' : name);

/** «هزینهٔ سونوگرافی: ۱٬۵۰۰٬۰۰۰ تا ۲٬۰۰۰٬۰۰۰ ⟨toman sign⟩» under the suggestion heading (config.price). */
export const PriceLine = ({ price }: { price: { service: string; min: number; max: number } }) => (
  <span className="flex flex-wrap items-center gap-1 text-xs text-slate-600" data-testid="local-specialists-price">
    هزینهٔ {price.service}:
    <TomanPrice className="font-medium text-slate-800">
      {price.min === price.max ? faNumber(price.min) : `${faNumber(price.min)} تا ${faNumber(price.max)}`}
    </TomanPrice>
  </span>
);

const AwardIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
    <circle cx="12" cy="9" r="6" />
    <path d="M9 14.5L8 22l4-2 4 2-1-7.5" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

interface SuggestionCarouselProps {
  doctors: CarouselDoctor[];
  onOpen: (id: string, element: 'card' | 'button') => void;
  more: { text: string; onClick: () => void };
}

/**
 * One suggested doctor: badges (preferred / insurance), avatar, name, specialty, rating; then waiting time, the full
 * insurance list, the first turn and the button. As tall as its own content. Booking page carousel and profile card.
 */
export const SuggestionCard = ({
  doctor,
  onOpen,
  className,
}: {
  doctor: CarouselDoctor;
  onOpen: (element: 'card' | 'button') => void;
  className?: string;
}) => (
  <div
    data-testid="suggestion-card"
    className={classNames(
      'flex flex-col gap-3 p-4 bg-white border border-solid rounded-xl cursor-pointer',
      doctor.badge ? 'border-primary/40' : 'border-slate-200',
      className,
    )}
    onClick={event => !(event.target as HTMLElement).closest('button') && onOpen('card')}
  >
    <div className="flex flex-col gap-3">
      {(doctor.badge || hasInsurance(doctor)) && (
        // one row of small pills that never wraps: the insurance pill shrinks (… at worst) before it would break
        <div className="flex items-center min-w-0 gap-1.5">
          {doctor.badge && (
            <span
              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full shrink-0 whitespace-nowrap text-primary bg-primary/10"
              data-testid="suggestion-badge"
            >
              <AwardIcon />
              {doctor.badge}
            </span>
          )}
          {hasInsurance(doctor) && (
            <span
              className="flex items-center min-w-0 gap-1 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap text-slate-700 bg-slate-100"
              data-testid="suggestion-insurance-badge"
            >
              <ShieldIcon />
              <span className="truncate">بیمه {shortInsurance(doctor.insurances?.[0] ?? 'تکمیلی')}</span>
              {insuranceCount(doctor) > 1 && (
                // «+۱» stays «+۱» inside right-to-left text
                <span dir="ltr" className="shrink-0">
                  +{faNumber(insuranceCount(doctor) - 1)}
                </span>
              )}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <Avatar
            src={getImageUrl(doctor.image, cdnBaseUrl())}
            alt={[doctor.name, doctor.expertise].filter(Boolean).join(' ')}
            width={64}
            height={64}
            className={doctor.isVerify ? 'border-2 border-primary' : 'border-2 border-slate-200'}
          />
          {doctor.isVerify && <VerifyIcon width={20} height={20} className="absolute bottom-0 left-0 fill-primary" />}
        </div>
        <div className="flex flex-col min-w-0 gap-1">
          {/* a long name wraps to a second line instead of being cut */}
          <span className="text-base font-bold leading-6 text-slate-800">{doctor.name}</span>
          {doctor.expertise && <span className="text-xs truncate text-slate-500">{doctor.expertise}</span>}
          {(doctor.ratesCount ?? 0) > 0 && (
            <span className="flex items-center gap-1 text-xs whitespace-nowrap">
              <LikeIcon width={16} height={16} className="text-primary" />
              <b className="font-bold text-primary">{doctor.satisfaction}٪ رضایت</b>
              <span className="text-slate-500">({doctor.ratesCount} نظر)</span>
            </span>
          )}
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-2">
      {doctor.waitingTitle && (
        <span className="flex items-center min-w-0 gap-1 text-xs text-slate-600" data-testid="suggestion-waiting">
          <span className="flex text-slate-500">
            <ClockIcon />
          </span>
          <span className="truncate">زمان انتظار در مطب: {doctor.waitingTitle}</span>
        </span>
      )}
      {hasInsurance(doctor) && (
        // the full list, wrapping to more lines when long — the badge above is the one-line summary
        <span className="flex items-start min-w-0 gap-1 text-xs leading-5 text-slate-600" data-testid="suggestion-insurances">
          <span className="flex mt-0.5 text-slate-500">
            <ShieldIcon />
          </span>
          <span>
            بیمه: {doctor.insurances?.length ? doctor.insurances.join('، ') : 'تکمیلی'}
            {!!doctor.insurances?.length && !!doctor.supplementCount && (
              <span className="whitespace-nowrap">{` + ${faNumber(doctor.supplementCount)} تکمیلی`}</span>
            )}
          </span>
        </span>
      )}
      {doctor.line && (
        <span className="self-start max-w-full px-2 py-1 text-xs font-medium truncate text-green-700 rounded-md bg-green-50">
          {doctor.line}
        </span>
      )}
      <Button block onClick={() => onOpen('button')}>
        {doctor.buttonText}
      </Button>
    </div>
  </div>
);

/**
 * Booking page (config.inlineOnBookingError), inside its own white card: a horizontal carousel scrolling edge to
 * edge, compact bordered cards — avatar, name on one line, specialty and rating beside it; the first turn as a green
 * chip and the button under it — so the cards stay the same height whatever the name.
 */
export const SuggestionCarousel = ({ doctors, onOpen, more }: SuggestionCarouselProps) => (
  // items-start: every card is as tall as its own content — a card with fewer lines is shorter, never padded with an
  // empty gap to the tallest one's height
  <ScrollContainer className="flex items-start gap-3 -mx-5 px-5 pb-1 !overflow-auto select-none no-scroll">
    {doctors.map(doctor => (
      <SuggestionCard
        key={doctor.id}
        doctor={doctor}
        onOpen={element => onOpen(doctor.id, element)}
        className="w-[17.5rem] min-w-[17.5rem]"
      />
    ))}
    <div
      className="flex flex-col items-center self-stretch justify-center w-[7.5rem] min-w-[7.5rem] gap-2 px-3 border border-solid rounded-xl cursor-pointer border-slate-200 text-primary"
      onClick={more.onClick}
    >
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10">
        <BackIcon className="rotate-180 shrink-0" />
      </span>
      <span className="text-xs font-medium leading-5 text-center">{more.text}</span>
    </div>
  </ScrollContainer>
);

export default SuggestionCarousel;
